import React, { forwardRef, useEffect, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import cn from 'classnames';
import { useComposeRef } from 'rc-util/es/ref';

import SearchIcon from 'common/components/Icon/Search';
import { tid, TIDValue } from 'common/utils/test-ids';

import css from './SearchField.scss';

/**
 * The look follows the place: `expandable` for list page headers, `filled` for a search that stands among other
 * controls, `embedded` for a search inside a host that draws the surface (a toolbar, or a list header with `divider`).
 * Only these variant and size pairs exist. Colors come from the `--search-*` variables of the host (see `_theme.scss`)
 */
type SearchFieldLayout =
	| { variant: 'expandable'; size?: 'lg' }
	| { variant: 'filled'; size: 'sm' | 'lg' | 'xl' }
	| { variant: 'embedded'; size: 'sm' | 'md'; divider?: boolean };

type Props = SearchFieldLayout & {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	'aria-label'?: string;
	autoFocus?: boolean;
	className?: string;
	testId?: TIDValue;
	/** Takes the search icon's place, e.g. a menu header's back button; it keeps the icon's slot and size. */
	leading?: ReactNode;
	/**
	 * The host's attributes and handlers for the input, such as a combobox's aria-* and its key handling. Its
	 * handlers run before the field's own, and a key event it prevents is not handled by the field.
	 */
	inputProps?: Omit<
		InputHTMLAttributes<HTMLInputElement>,
		'value' | 'onChange' | 'placeholder' | 'type' | 'className'
	>;
};

const ClearIcon = () => (
	<svg viewBox="0 0 12 12" aria-hidden="true">
		<path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
	</svg>
);

export const SearchField = forwardRef<HTMLInputElement, Props>((props, ref) => {
	const {
		variant,
		value,
		onChange,
		placeholder = 'Search',
		'aria-label': ariaLabel = placeholder,
		autoFocus,
		className,
		testId,
		leading,
		inputProps,
	} = props;
	const size = props.size ?? 'lg';
	const divider = props.variant === 'embedded' && props.divider;
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [isFocused, setIsFocused] = useState(false);
	const isExpanded = variant === 'expandable' && (isFocused || value !== '');

	const setInputRef = useComposeRef(inputRef, ref);

	useEffect(() => {
		if (autoFocus) inputRef.current?.focus({ preventScroll: true });
	}, [autoFocus]);

	const focusInput = () => inputRef.current?.focus({ preventScroll: true });

	const clear = () => {
		onChange('');
		focusInput();
	};

	// The first Escape clears the field; with an empty field it reaches the host, which may close a popover
	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		inputProps?.onKeyDown?.(event);
		if (event.defaultPrevented || event.key !== 'Escape' || value === '') return;
		event.stopPropagation();
		onChange('');
	};

	// Keep the focus in the input while its buttons are pressed, so an open expandable field does not fold
	const keepFocus = (event: React.MouseEvent) => {
		if (isFocused) event.preventDefault();
	};

	const icon = <SearchIcon className={css.icon} color="currentColor" aria-hidden="true" />;

	return (
		<div
			className={cn(
				css.searchField,
				css[variant],
				css[size],
				isExpanded && css.open,
				isFocused && css.focused,
				divider && css.divider,
				className
			)}
		>
			{variant === 'expandable' ? (
				<>
					<div className={css.expandableBg} />
					<button
						type="button"
						className={css.expandableBtn}
						aria-label={ariaLabel}
						onMouseDown={keepFocus}
						onClick={focusInput}
					>
						{icon}
					</button>
				</>
			) : (
				leading ?? icon
			)}
			<input
				aria-label={ariaLabel}
				{...inputProps}
				ref={setInputRef}
				className={css.input}
				type="text"
				value={value}
				placeholder={placeholder}
				onChange={event => onChange(event.target.value)}
				onKeyDown={onKeyDown}
				onFocus={event => {
					inputProps?.onFocus?.(event);
					setIsFocused(true);
				}}
				onBlur={event => {
					inputProps?.onBlur?.(event);
					setIsFocused(false);
				}}
				{...(testId ? tid(testId) : {})}
			/>
			{/* Hidden, not unmounted: a host that closes on an outside click must still find the clicked button */}
			<button
				type="button"
				className={cn(css.clear, value === '' && css.clearHidden)}
				aria-label="Clear search"
				onMouseDown={keepFocus}
				onClick={clear}
			>
				<ClearIcon />
			</button>
		</div>
	);
});

SearchField.displayName = 'SearchField';

export default SearchField;
